import type { LimitationItem } from '../components/papers/types';

export interface LimitationBlock {
    title: string;
    description: string;
}

const explanationLabel = /^(?:why\s+it\s+matters|this\s+matters\s+because)\s*:?$/i;
const genericTitle = /^(?:study\s+)?limitations?\s*:?$/i;

function cleanTitle(text: string): string {
    return text.trim().replace(/:\s*$/, '').replace(/^[*_]+|[*_]+$/g, '').replace(/:\s*$/, '').trim();
}

function cleanDescription(text: string): string {
    // Only remove paired emphasis, not multiplication, underscores or LaTeX.
    return text.trim()
        .replace(/\*\*([^*\n]+)\*\*/g, '$1')
        .replace(/__([^_\n]+)__/g, '$1')
        .replace(/(?:^|\s)(?:[-+*•][ \t]+|#{1,6}[ \t]+)(?=(?:why\s+it\s+matters|this\s+matters\s+because)\b)/gi, '\n\n')
        .trim()
        .replace(/^(?:[-+*•]\s+)?(?:why\s+it\s+matters|this\s+matters\s+because)\s*:\s*/i, '')
        .trim();
}

/**
 * Recover display blocks from LLM lists, including lists flattened by older
 * backend parsers. No titles are inferred from prose and no source data is mutated.
 * Citations belong to the original row, so callers keep them with the whole group.
 */
export function parseLimitationContent(
    limitation: Pick<LimitationItem, 'title' | 'description'>,
): LimitationBlock[] {
    const text = limitation.description.replace(/\r\n?/g, '\n');
    const title = cleanTitle(limitation.title);
    const fallbackTitle = explanationLabel.test(title) ? '' : title;
    const headings: { start: number; end: number; title: string }[] = [];

    // Bold titles may follow a bullet even when all newlines were lost. Accept a
    // single closing marker too: old cleanup can turn **Title** into **Title*.
    // Unmarked titles require an explicit list marker/heading at a line boundary.
    const headingPattern = /(^[ \t]*(?:(?:[-+*•]|\d+[.)])[ \t]+)?|[ \t]+(?:[-+*•]|\d+[.)])[ \t]+)(?:\*{2,}|_{2,})([^\n*_]+?)(?:\*+|_+)(?=[ \t:\n–—-]|$|Why\s+it\s+matters|This\s+matters\s+because)[ \t]*:?[ \t]*|^[ \t]*#{1,6}[ \t]+([^\n]+)|^[ \t]*(?:[-+*•]|\d+[.)])[ \t]+([^\n:]+):[ \t]*/gim;

    for (const match of text.matchAll(headingPattern)) {
        if (match[2]) {
            const hasListMarker = /(?:[-+*•]|\d+[.)])[ \t]+$/.test(match[1]);
            const hasColon = /:\s*$/.test(match[2]) || /:[ \t]*$/.test(match[0]);
            const remainder = text.slice(match.index + match[0].length);
            const isHeadingLine = /^(?:\n|$)/.test(remainder);
            const hasExplanation = /^(?:why\s+it\s+matters|this\s+matters\s+because)\s*:/i.test(remainder);
            // Bold prose at the start of a paragraph is not necessarily a title.
            if (!hasListMarker && !hasColon && !isHeadingLine && !hasExplanation) continue;
        }
        const heading = cleanTitle(match[2] ?? match[3] ?? match[4]);
        // Nested explanation labels are content, never new limitations.
        if (explanationLabel.test(heading)) continue;
        headings.push({ start: match.index, end: match.index + match[0].length, title: heading });
    }

    if (headings.length === 0) {
        return [{ title: fallbackTitle, description: cleanDescription(text) }];
    }

    const blocks: LimitationBlock[] = [];
    const prefix = cleanDescription(text.slice(0, headings[0].start));
    if (prefix) blocks.push({ title: fallbackTitle, description: prefix });
    else if (fallbackTitle && !genericTitle.test(fallbackTitle) && fallbackTitle !== headings[0].title) {
        // Keep meaningful outer headings instead of silently discarding them.
        blocks.push({ title: fallbackTitle, description: '' });
    }

    headings.forEach((heading, index) => {
        const description = text.slice(heading.end, headings[index + 1]?.start ?? text.length);
        blocks.push({ title: heading.title, description: cleanDescription(description) });
    });
    return blocks;
}
